import {
  GuestLiteModel,
  GuestModel,
  getGuestsKey,
  useGetGuests,
} from 'api-hooks/guest';
import { Option } from 'baseui/select';
import { NavigationRoutes } from 'common/constants';
import { capitalize, transformLabel } from 'common/utils/string';
import { Input } from 'components/elements';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useMultiSelectInputHelper from 'hooks/use-multi-select-input-helper';
import useNavigation from 'hooks/use-navigation';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

interface Props {
  name: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  onClose?: () => void;
  onAfterChange?: (value?: any) => void;
  noMargin?: boolean;
  type?: string;
  fieldType?: 'control' | 'uncontrol';
}

export function guestMultiTransformer(
  item: GuestModel | GuestLiteModel,
): Option {
  return {
    id: item.id,
    label: transformLabel([capitalize(item.title), item.name], ' '),
  };
}

export default function GuestMultiInput(props: Props) {
  const { t } = useTranslation();
  const {
    label = t('common:guest'),
    placeholder = t('common:select_guest'),
    type,
  } = props;

  const { control, setValue } = useFormContext<any>();
  const permission = useGetCrudPermission('guests');
  const value = useWatch({ control, name: props.name });
  const { navigate } = useNavigation();
  const { overrides, ...selectProps } = useMultiSelectInputHelper({
    value,
    onSelectItem: (value) => {
      setValue(props.name, value || [], {
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    useListQueryHook: useGetGuests,
    listKeyFunction: getGuestsKey,
    getMemoizedListVariables: React.useCallback(
      (page, q) => ({
        params: {
          q,
          page,
          type,
        },
      }),
      [type],
    ),

    renderCreate: permission.create,
    onClickCreate() {
      navigate(NavigationRoutes.GuestsCreate);
      props.onClose?.();
    },
    listTransformer: React.useCallback((result) => {
      return (result.data || []).map((v) => guestMultiTransformer(v));
    }, []),
    paginationTransformer: React.useCallback((result) => {
      return result.meta;
    }, []),
  });

  return (
    <Input
      type="select"
      name={props.name}
      required={props.required}
      label={label}
      placeholder={placeholder}
      onAfterChange={props.onAfterChange}
      noMargin={props.noMargin}
      fieldType={props.fieldType}
      multi
      filterOptions={null}
      {...selectProps}
    />
  );
}
